import { useTranslation } from '@rbx/intl';
import React, { FC, useMemo, useState } from 'react';
import { Typography } from '@rbx/ui';
import ResponsiveTooltip from '@modules/miscellaneous/common/components/ResponsiveTooltip';
import useDeviceInfo from '@modules/miscellaneous/common/hooks/useDeviceInfo';
import unifiedLoggerClient from '@modules/eventStream/implementations/unifiedLoggerClient';
import DocsEngineReferenceEvent from '@modules/eventStream/enum/DocsEngineReferenceEvent';
import DynamicallyRouted from '@modules/miscellaneous/common/components/DynamicallyRouted';
import { TypeAliasDatumItem } from '../../types';
import tooltipUXProps from '../../constants/tooltipUXProps';
import useReferenceSummaryMemberTableStyles from './ReferenceSummaryMemberTable.styles';
import useReferenceSummaryMemberTableStylesV2 from './ReferenceSummaryMemberTableV2.styles';
import ReferenceMemberTypeTooltipTitle, {
  clickableMemberTypeTooltipContentDataTooltipId,
} from './ReferenceMemberTypeTooltipTitle';

interface ReferenceMemberTypeItemProps {
  url: string | undefined;
  label: string;
  tooltipProps: TypeAliasDatumItem['tooltipProps'];
  engineRevampEnabled?: boolean;
}

const ReferenceMemberTypeItem: FC<React.PropsWithChildren<ReferenceMemberTypeItemProps>> = ({
  url,
  label,
  tooltipProps,
  engineRevampEnabled = false,
}) => {
  const { isMobileDevice } = useDeviceInfo();
  const [isOpen, setIsOpen] = useState(false);
  const { translate } = useTranslation();
  const defaultStyles = useReferenceSummaryMemberTableStyles();
  const v2Styles = useReferenceSummaryMemberTableStylesV2();
  const { classes, cx } = engineRevampEnabled ? v2Styles : defaultStyles;

  const trigger = useMemo(() => {
    // trigger is a button to open tooltip on mobile, link on desktop
    const triggerEl = (
      <Typography
        variant={engineRevampEnabled ? 'body2' : 'body1'}
        component='span'
        aria-roledescription={translate('Label.Tooltip') || 'Tooltip'}
        aria-label={translate('Label.ParameterType') || 'Parameter type'}
        className={cx(classes.parameterType, 'dashedType')}>
        {label}
      </Typography>
    );

    const content =
      !url || isMobileDevice ? (
        triggerEl
      ) : (
        <DynamicallyRouted.Link href={url}>{triggerEl}</DynamicallyRouted.Link>
      );

    // Wrapper needed for MUI Tooltip to attach hover events
    return <span>{content}</span>;
  }, [isMobileDevice, url, translate, label, engineRevampEnabled, classes.parameterType, cx]);

  return typeof tooltipProps === 'object' ? (
    <ResponsiveTooltip
      isOpen={isOpen}
      setIsOpen={setIsOpen}
      onOpen={() => {
        unifiedLoggerClient.logImpressionEvent({
          eventName: DocsEngineReferenceEvent.OpenTypeTooltip,
          parameters: {
            label,
            contentClickableLink: url ?? '',
          },
        });
      }}
      clickableTooltipContentDataTooltipId={clickableMemberTypeTooltipContentDataTooltipId}
      arrow
      placement='bottom'
      TransitionProps={{ timeout: 0 }}
      title={
        <ReferenceMemberTypeTooltipTitle
          url={url}
          tooltipProps={tooltipProps}
          engineRevampEnabled={engineRevampEnabled}
        />
      }
      engineRevampEnabled={engineRevampEnabled}
      {...tooltipUXProps}>
      {trigger}
    </ResponsiveTooltip>
  ) : (
    trigger
  );
};

ReferenceMemberTypeItem.displayName = 'ReferenceMemberTypeItem';

export default ReferenceMemberTypeItem;
